'use client';

/**
 * Register Form Component
 * 
 * Core component for customer registration.
 * Can be styled by themes using className props and CSS variables.
 */

import { useState, useCallback, useRef, type FormEvent } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCustomerAuth } from './CustomerAuthProvider';
import { validateRedirectPath } from '@/lib/security/validate-redirect';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTenantOptional } from '@/contexts/TenantContext';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { TurnstileWidget, type TurnstileRef } from '@/components/ui/TurnstileWidget';

// ============================================================================
// Types
// ============================================================================

export interface RegisterFormProps {
  /** Callback after successful registration */
  onSuccess?: (requiresEmailVerification: boolean) => void;
  /** Redirect URL after registration (if no email verification required) */
  redirectTo?: string;
  /** Show "Already have an account" link */
  showLoginLink?: boolean;
  /** Login page URL */
  loginUrl?: string;
  /** Show social auth buttons */
  showSocialAuth?: boolean;
  /** Minimum password length */
  minPasswordLength?: number;
  /** Custom class name for styling */
  className?: string;
  /** Custom title */
  title?: string;
  /** Custom subtitle */
  subtitle?: string;
}

// ============================================================================
// Component
// ============================================================================

export function RegisterForm({
  onSuccess,
  redirectTo = '/account',
  showLoginLink = true,
  loginUrl = '/login',
  showSocialAuth = true,
  minPasswordLength = 8,
  className = '',
  title = 'Create an Account',
  subtitle = 'Join us to save properties and track your enquiries',
}: RegisterFormProps) {
  const { signUp, signInWithOAuth, error, clearError } = useCustomerAuth();
  const router = useRouter();
  const tenant = useTenantOptional();
  const locale = tenant?.locale || 'en';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileRef>(null);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < minPasswordLength) {
      errors.push(`Password must be at least ${minPasswordLength} characters`);
    }
    if (!/[A-Z]/.test(pwd)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(pwd)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Clear previous errors
    setFormError(null);
    clearError();

    // Validation
    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setFormError('Please enter a valid email address');
      return;
    }

    if (!password) {
      setFormError('Password is required');
      return;
    }

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setFormError(passwordErrors[0] ?? 'Invalid password');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signUp({
        email: email.trim(),
        password,
        firstName: firstName.trim() ? firstName.trim() : undefined,
        lastName: lastName.trim() ? lastName.trim() : undefined,
        captchaToken: turnstileToken || undefined,
      });

      analyticsEvents.signUp('email');
      analyticsEvents.generateLead('registration');

      if (result.requiresEmailVerification) {
        setShowEmailVerification(true);
        onSuccess?.(true);
      } else {
        onSuccess?.(false);
        const safeRedirect = validateRedirectPath(redirectTo, `/${locale}/account`);
        const redirectParam = safeRedirect ? `?redirect=${encodeURIComponent(safeRedirect)}` : '';
        router.push(`/${locale}/setup-2fa${redirectParam}`);
      }
    } catch {
      // Error is handled by the auth provider
      // Reset Turnstile — tokens are single-use
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOAuthSignup = async (provider: 'google') => {
    try {
      analyticsEvents.signUp(provider);
      await signInWithOAuth(provider, redirectTo);
    } catch (err) {
      console.error('[RegisterForm] OAuth failed:', err);
    }
  };

  const displayError = formError ?? error?.message ?? null;
  // Only disable during active submit - not during initial auth check (isLoading).
  // Auth check can take 1–2s; blocking interaction on load is poor UX.
  const isDisabled = isSubmitting;

  // Show email verification message
  if (showEmailVerification) {
    return (
      <div className={`register-form register-form--verification ${className}`}>
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-semibold text-stone-900 mb-2">
            Check your email
          </h1>
          <p className="text-stone-500 mb-6">
            We&apos;ve sent a verification link to <strong className="text-stone-700">{email}</strong>.
            Click the link to complete your registration.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => setShowEmailVerification(false)}
              className="w-full h-12 rounded-xl border border-stone-200 bg-white text-stone-700 font-medium hover:bg-stone-50 hover:border-stone-300 transition-all duration-200"
            >
              Use a different email
            </button>
            <Link
              href={loginUrl}
              className="block w-full h-12 rounded-xl bg-stone-900 text-white font-semibold flex items-center justify-center hover:bg-stone-800 transition-all duration-200 shadow-lg shadow-stone-900/10"
            >
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`register-form ${className}`}>
      {/* Header */}
      <div className="register-form__header mb-8">
        <h1 className="register-form__title text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          {title}
        </h1>
        <p className="register-form__subtitle text-base text-stone-500 mt-2">
          {subtitle}
        </p>
      </div>

      {/* Error Alert */}
      {displayError && (
        <div className="register-form__error mb-6 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-600">{displayError}</p>
        </div>
      )}

      {/* Social Auth */}
      {showSocialAuth && (
        <>
          <div className="register-form__social space-y-3">
            <button
              type="button"
              onClick={() => handleOAuthSignup('google')}
              disabled={isDisabled}
              className="register-form__social-btn w-full flex items-center justify-center gap-3 h-12 px-4 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 hover:border-stone-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <GoogleIcon className="h-5 w-5" />
              <span className="text-sm font-medium text-stone-700">Continue with Google</span>
            </button>
          </div>

          {/* Divider */}
          <div className="register-form__divider relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-sm text-stone-400 font-medium">
                or continue with email
              </span>
            </div>
          </div>
        </>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="register-form__form space-y-5">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="register-form__field">
            <label
              htmlFor="register-first-name"
              className="register-form__label block text-sm font-medium text-stone-700 mb-2"
            >
              First name
            </label>
            <input
              id="register-first-name"
              name="firstName"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              disabled={isDisabled}
              className="register-form__input w-full h-12 px-4 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div className="register-form__field">
            <label
              htmlFor="register-last-name"
              className="register-form__label block text-sm font-medium text-stone-700 mb-2"
            >
              Last name
            </label>
            <input
              id="register-last-name"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              disabled={isDisabled}
              className="register-form__input w-full h-12 px-4 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Email */}
        <div className="register-form__field">
          <label
            htmlFor="register-email"
            className="register-form__label block text-sm font-medium text-stone-700 mb-2"
          >
            Email address <span className="text-red-500">*</span>
          </label>
          <input
            id="register-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            disabled={isDisabled}
            className="register-form__input w-full h-12 px-4 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Password */}
        <div className="register-form__field">
          <label
            htmlFor="register-password"
            className="register-form__label block text-sm font-medium text-stone-700 mb-2"
          >
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="register-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a strong password"
              disabled={isDisabled}
              className="register-form__input w-full h-12 px-4 pr-12 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          <p className="mt-2 text-xs text-stone-400">
            At least {minPasswordLength} characters with uppercase, lowercase, and a number
          </p>
        </div>

        {/* Confirm Password */}
        <div className="register-form__field">
          <label
            htmlFor="register-confirm-password"
            className="register-form__label block text-sm font-medium text-stone-700 mb-2"
          >
            Confirm password <span className="text-red-500">*</span>
          </label>
          <input
            id="register-confirm-password"
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            disabled={isDisabled}
            className="register-form__input w-full h-12 px-4 rounded-xl border border-stone-200 bg-stone-50/50 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Terms */}
        <p className="text-xs text-stone-400">
          By creating an account, you agree to our{' '}
          <Link href={`/${locale}/terms`} className="text-amber-600 hover:text-amber-700 hover:underline transition-colors">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link href={`/${locale}/privacy`} className="text-amber-600 hover:text-amber-700 hover:underline transition-colors">
            Privacy Policy
          </Link>
          .
        </p>

        {/* Turnstile Bot Protection */}
        <TurnstileWidget
          ref={turnstileRef}
          onVerify={handleTurnstileVerify}
          onExpire={handleTurnstileExpire}
          className="flex justify-center"
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isDisabled}
          className="register-form__submit w-full h-12 rounded-xl bg-stone-900 text-white font-semibold hover:bg-stone-800 focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-stone-900/10"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Creating account...
            </span>
          ) : (
            'Create account'
          )}
        </button>
      </form>

      {/* Login Link */}
      {showLoginLink && (
        <p className="register-form__login mt-8 text-center text-sm text-stone-500">
          Already have an account?{' '}
          <Link
            href={loginUrl}
            className="font-semibold text-amber-600 hover:text-amber-700 hover:underline transition-colors"
          >
            Sign in
          </Link>
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default RegisterForm;
